/**
 * FlowEmptyState - Empty state component for the Flow page
 *
 * Displays appropriate messaging based on why the flow diagram is empty,
 * with CTAs to help users take the next action.
 */

import Button from '@/components/ui/Button';
import Spinner from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';

export type FlowEmptyStateReason =
  | 'no-accounts'
  | 'no-transactions'
  | 'no-data-in-range'
  | 'loading'
  | 'error';

export interface FlowEmptyStateProps {
  reason: FlowEmptyStateReason;
  periodLabel?: string;
  onConnectAccount?: () => void;
  onChangePeriod?: () => void;
  onRetry?: () => void;
  /**
   * WP-CORE-9: with reason 'error', the flow was refused by the household's
   * calculation limit (429); this message (when to retry) replaces the
   * generic error text.
   */
  rateLimitMessage?: string | null;
  className?: string;
}

/**
 * Icon components for each empty state type
 */
const NoAccountsIcon = () => (
  <svg
    className="h-16 w-16 text-gray-400"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
    />
  </svg>
);

const NoTransactionsIcon = () => (
  <svg
    className="h-16 w-16 text-gray-400"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
    />
  </svg>
);

const NoDataInRangeIcon = () => (
  <svg
    className="h-16 w-16 text-gray-400"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const ErrorIcon = () => (
  <svg
    className="h-16 w-16 text-red-400"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
    />
  </svg>
);

/**
 * Content configuration for each empty state reason
 */
interface EmptyStateContent {
  icon: React.ReactNode;
  title: string;
  description: string;
  actionLabel?: string;
}

function getEmptyStateContent(
  reason: FlowEmptyStateReason,
  periodLabel?: string
): EmptyStateContent {
  switch (reason) {
    case 'no-accounts':
      return {
        icon: <NoAccountsIcon />,
        title: 'Connect a bank account to see your money flow',
        description:
          'Link your bank accounts, credit cards, or investment accounts to visualize how money moves through your finances.',
        actionLabel: 'Connect Account',
      };

    case 'no-transactions':
      return {
        icon: <NoTransactionsIcon />,
        title: 'No transactions yet',
        description:
          'Your transactions will appear here once they sync. This usually takes a few minutes after connecting an account.',
      };

    case 'no-data-in-range':
      return {
        icon: <NoDataInRangeIcon />,
        title: periodLabel
          ? `No transactions found for ${periodLabel}`
          : 'No transactions in this date range',
        description: 'Try selecting a different date range to see your money flow.',
        actionLabel: 'Change Date Range',
      };

    case 'loading':
      return {
        icon: <Spinner size="lg" />,
        title: 'Loading your flow...',
        description: 'We are analyzing your transactions to build your flow visualization.',
      };

    case 'error':
      return {
        icon: <ErrorIcon />,
        title: 'Something went wrong',
        description:
          'We could not load your flow data. Please try again or contact support if the problem persists.',
        actionLabel: 'Try Again',
      };

    default:
      return {
        icon: <NoTransactionsIcon />,
        title: 'No data available',
        description: 'Connect accounts and add transactions to see your flow visualization.',
      };
  }
}

export default function FlowEmptyState({
  reason,
  periodLabel,
  onConnectAccount,
  onChangePeriod,
  onRetry,
  rateLimitMessage = null,
  className,
}: FlowEmptyStateProps) {
  const baseContent = getEmptyStateContent(reason, periodLabel);
  const content =
    reason === 'error' && rateLimitMessage
      ? { ...baseContent, title: 'Flow limit reached', description: rateLimitMessage }
      : baseContent;

  // Determine which action handler to use based on reason
  const getActionHandler = () => {
    switch (reason) {
      case 'no-accounts':
        return onConnectAccount;
      case 'no-data-in-range':
        return onChangePeriod;
      case 'error':
        return onRetry;
      default:
        return undefined;
    }
  };

  const actionHandler = getActionHandler();
  const showAction = content.actionLabel && actionHandler;

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center h-full min-h-[400px] px-4',
        className
      )}
      role="status"
      aria-live="polite"
    >
      {/* Icon container */}
      <div
        className={cn(
          'rounded-full p-6 mb-6',
          reason === 'loading' ? 'bg-primary-50' : 'bg-gray-50',
          reason === 'error' && 'bg-red-50'
        )}
      >
        {content.icon}
      </div>

      {/* Title */}
      <h2
        className={cn(
          'text-xl font-semibold mb-2 text-center',
          reason === 'error' ? 'text-red-900' : 'text-gray-900'
        )}
      >
        {content.title}
      </h2>

      {/* Description */}
      <p className="text-gray-500 text-center max-w-md mb-6">{content.description}</p>

      {/* Action button */}
      {showAction && (
        <Button
          onClick={actionHandler}
          variant={reason === 'error' ? 'outline' : 'primary'}
          size="lg"
        >
          {reason === 'no-accounts' && (
            <svg
              className="h-5 w-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
          )}
          {reason === 'error' && (
            <svg
              className="h-5 w-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          )}
          {content.actionLabel}
        </Button>
      )}

      {/* Additional help text for loading state */}
      {reason === 'loading' && (
        <p className="text-sm text-gray-400 mt-4">
          This may take a few seconds for large datasets.
        </p>
      )}
    </div>
  );
}
